import { useCallback, useEffect, useMemo, useState } from 'react'
import { Compteur } from '../Compteur'
import { listerCandidaturesLivreurs, listerCandidaturesMarchands } from '../../api/candidatures'
import { messageErreur } from '../../onboarding/erreurs'
import {
  LIBELLE_STATUT,
  STATUTS_HORS_PARCOURS,
  STATUTS_LIVREUR,
  STATUTS_MARCHAND,
  prochaineActionStaff,
} from '../../onboarding/etapes'
import type { CandidatureLivreur, CandidatureMarchand } from '../../onboarding/types'
import { dateCourte } from '../Format'
import { Panneau } from '../Modale'
import { BadgeStatut } from '../candidatures/Briques'
import { FicheLivreur } from '../candidatures/FicheLivreur'
import { FicheMarchand } from '../candidatures/FicheMarchand'
import { ModelesContrat } from '../candidatures/ModelesContrat'

/**
 * Candidatures — intégration des prestataires (migration 0045).
 *
 * Deux sous-onglets, Livreurs et Commerçants : tableau filtrable par statut
 * (pastilles de comptage), recherche par nom, clic → fiche dossier en tiroir
 * large. Les statuts avancent en grande partie tout seuls (déclencheurs de la
 * base) ; le staff ne tranche que les pièces, l'entretien, la formation, les
 * contrats et la décision finale.
 */
type SousOnglet = 'livreurs' | 'commercants'

const filtreVide = 'tous'

/** Les statuts à proposer en filtre, dans l'ordre du parcours (+ refus et suspension). */
const STATUTS_FILTRE: Record<SousOnglet, readonly string[]> = {
  livreurs: [...STATUTS_LIVREUR, ...STATUTS_HORS_PARCOURS],
  commercants: [...STATUTS_MARCHAND, ...STATUTS_HORS_PARCOURS],
}

const normaliser = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

export function EcranCandidatures({ estAdmin }: { estAdmin: boolean }) {
  const [onglet, setOnglet] = useState<SousOnglet>('livreurs')
  const [livreurs, setLivreurs] = useState<CandidatureLivreur[]>([])
  const [marchands, setMarchands] = useState<CandidatureMarchand[]>([])
  const [erreurs, setErreurs] = useState<Record<SousOnglet, string | null>>({ livreurs: null, commercants: null })
  const [filtre, setFiltre] = useState<string>(filtreVide)
  const [recherche, setRecherche] = useState('')
  const [selection, setSelection] = useState<string | null>(null)
  const [modeles, setModeles] = useState(false)

  const charger = useCallback(async () => {
    /* Deux lectures INDÉPENDANTES : l'échec de l'une ne vide pas l'autre. */
    const [l, m] = await Promise.allSettled([listerCandidaturesLivreurs(), listerCandidaturesMarchands()])
    if (l.status === 'fulfilled') setLivreurs(l.value)
    if (m.status === 'fulfilled') setMarchands(m.value)
    setErreurs({
      livreurs: l.status === 'rejected' ? messageErreur(l.reason) : null,
      commercants: m.status === 'rejected' ? messageErreur(m.reason) : null,
    })
  }, [])

  useEffect(() => {
    void charger()
  }, [charger])

  const lignes: Array<CandidatureLivreur | CandidatureMarchand> = onglet === 'livreurs' ? livreurs : marchands

  const comptes = useMemo(() => {
    const c: Record<string, number> = {}
    for (const l of lignes) c[l.statut] = (c[l.statut] ?? 0) + 1
    return c
  }, [lignes])

  const visibles = useMemo(() => {
    const q = normaliser(recherche.trim())
    return lignes.filter(
      (l) => (filtre === filtreVide || l.statut === filtre) && (q === '' || normaliser(l.nom).includes(q)),
    )
  }, [lignes, filtre, recherche])

  const livreurSelectionne = onglet === 'livreurs' ? livreurs.find((l) => l.id === selection) ?? null : null
  const marchandSelectionne = onglet === 'commercants' ? marchands.find((m) => m.id === selection) ?? null : null
  const fiche = livreurSelectionne ?? marchandSelectionne

  const aTraiter = (statuts: string[]) => lignes.filter((l) => statuts.includes(l.statut)).length

  function changerOnglet(o: SousOnglet) {
    setOnglet(o)
    setFiltre(filtreVide)
    setRecherche('')
    setSelection(null)
  }

  return (
    <>
      <div className="stats">
        <div className="stat">
          <div className="label">Dossiers {onglet === 'livreurs' ? 'livreurs' : 'commerçants'}</div>
          <div className="val"><Compteur valeur={String(lignes.length)} /></div>
        </div>
        <div className="stat" title="Pièces, entretien, formation, contrat ou activation à traiter">
          <div className="label">En cours</div>
          <div className="val" style={{ color: 'var(--warning)' }}>
            <Compteur
              valeur={String(
                aTraiter(onglet === 'livreurs'
                  ? ['candidature', 'dossier_complet', 'entretien_fait', 'formation_faite', 'quiz_reussi', 'contrat_signe']
                  : ['prospect', 'documents', 'contrat_envoye', 'contrat_signe']),
              )}
            />
          </div>
        </div>
        <div className="stat">
          <div className="label">Actifs</div>
          <div className="val" style={{ color: 'var(--green)' }}><Compteur valeur={String(aTraiter(['actif']))} /></div>
        </div>
        <div className="stat">
          <div className="label">Refusés / suspendus</div>
          <div className="val" style={{ color: 'var(--danger)' }}>
            <Compteur valeur={String(aTraiter(['refuse', 'suspendu']))} />
          </div>
        </div>
      </div>

      <div className="subtabs">
        <button className={`subtab${onglet === 'livreurs' ? ' active' : ''}`} onClick={() => changerOnglet('livreurs')}>
          Livreurs <span className="chip-n">{livreurs.length}</span>
        </button>
        <button className={`subtab${onglet === 'commercants' ? ' active' : ''}`} onClick={() => changerOnglet('commercants')}>
          Commerçants <span className="chip-n">{marchands.length}</span>
        </button>
      </div>

      <div className="chip-row">
        <button className={`chip${filtre === filtreVide ? ' active' : ''}`} onClick={() => setFiltre(filtreVide)}>
          Tous <span className="chip-n">{lignes.length}</span>
        </button>
        {STATUTS_FILTRE[onglet].map((s) => (
          <button key={s} className={`chip${filtre === s ? ' active' : ''}`} onClick={() => setFiltre(s)}>
            {LIBELLE_STATUT[s] ?? s} <span className="chip-n">{comptes[s] ?? 0}</span>
          </button>
        ))}
      </div>

      <div className="search-bar">
        <input
          type="search"
          placeholder={`Rechercher un ${onglet === 'livreurs' ? 'livreur' : 'commerçant'} par nom…`}
          value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
          aria-label="Recherche par nom"
        />
      </div>

      {erreurs[onglet] && <p style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 10 }}>{erreurs[onglet]}</p>}

      <div className="card">
        <div className="card-header">
          {onglet === 'livreurs' ? 'Candidatures livreurs' : 'Candidatures commerçants'}
          <div className="spacer" style={{ flex: 1 }} />
          {estAdmin && (
            <button className="btn btn-outline btn-sm" onClick={() => setModeles(true)}>
              Modèles de contrat
            </button>
          )}
        </div>
        <div className="card-body">
          <table>
            <thead>
              <tr>
                <th>Nom</th>
                <th>Téléphone</th>
                <th>{onglet === 'livreurs' ? 'Véhicule' : 'Type'}</th>
                <th>Statut</th>
                <th>À faire</th>
                <th>Mis à jour</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visibles.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', color: 'var(--n400)', padding: 20 }}>
                    Aucun dossier ne correspond.
                  </td>
                </tr>
              )}
              {visibles.map((l) => {
                const action = prochaineActionStaff(onglet === 'livreurs' ? 'livreur' : 'commercant', l.statut)
                return (
                  <tr key={l.id}>
                    <td>
                      <a href="#" onClick={(e) => { e.preventDefault(); setSelection(l.id) }}>{l.nom}</a>
                    </td>
                    <td>{l.telephone || '—'}</td>
                    <td>{'vehicule' in l ? l.vehicule || '—' : l.type}</td>
                    <td><BadgeStatut statut={l.statut} /></td>
                    <td style={{ fontSize: 12, color: action.pourStaff ? 'var(--ink)' : 'var(--n400)', maxWidth: 260 }}>
                      {action.libelle}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>{dateCourte(l.majLe ?? l.creeLe)}</td>
                    <td>
                      <button className="btn btn-outline btn-sm" onClick={() => setSelection(l.id)}>Dossier</button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Panneau titre={fiche ? `Dossier — ${fiche.nom}` : ''} ouvert={Boolean(fiche)} large surFermer={() => setSelection(null)}>
        {livreurSelectionne && <FicheLivreur key={livreurSelectionne.id} cand={livreurSelectionne} surChange={() => void charger()} />}
        {marchandSelectionne && <FicheMarchand key={marchandSelectionne.id} cand={marchandSelectionne} surChange={() => void charger()} />}
      </Panneau>

      {estAdmin && <ModelesContrat ouvert={modeles} surFermer={() => setModeles(false)} />}
    </>
  )
}
